import { API_ENDPOINTS } from "@/configs";
import { track } from "@/lib/analytics";
import apiRequest from "@/lib/axios";
import { getDeviceKind } from "@/lib/device";
import {
  Game,
  GrantHistoryItem,
  GrantSummary,
  PlayerFreeSpins,
  PromoCodeResult,
  PublicCampaign,
  SkyClaim,
} from "@/types";

/** The player's unplayed free spins, and the games of the site that spend them. */
export const getMyFreeSpins = async (): Promise<{ spins: PlayerFreeSpins[]; games: Game[] }> =>
  apiRequest({
    method: "GET",
    url: API_ENDPOINTS.PROMOTION.FREE_SPINS,
    params: { device: getDeviceKind() },
    errorMessage: "Failed to load your free spins",
  });

export const getPromotionCampaigns = async (): Promise<PublicCampaign[]> => {
  return apiRequest({
    method: "GET",
    url: API_ENDPOINTS.PROMOTION.CAMPAIGNS,
    errorMessage: "Failed to load promotions",
  });
};

export const getMyGrants = async (): Promise<GrantSummary[]> => {
  return apiRequest({
    method: "GET",
    url: API_ENDPOINTS.PROMOTION.MY_GRANTS,
    errorMessage: "Failed to load your bonuses",
  });
};

export const getMyGrantHistory = async (): Promise<GrantHistoryItem[]> => {
  return apiRequest({
    method: "GET",
    url: API_ENDPOINTS.PROMOTION.MY_GRANTS_HISTORY,
    errorMessage: "Failed to load your bonus history",
  });
};

export const activateGrant = async (grantId: string): Promise<{ balance: number }> => {
  return apiRequest({
    method: "POST",
    url: API_ENDPOINTS.PROMOTION.ACTIVATE(grantId),
    errorMessage: "Failed to activate bonus",
  });
};

export const claimGrant = async (
  grantId: string,
): Promise<GrantSummary & { skyClaim?: SkyClaim }> => {
  return apiRequest<GrantSummary & { skyClaim?: SkyClaim }>({
    method: "POST",
    url: API_ENDPOINTS.PROMOTION.CLAIM(grantId),
    errorMessage: "Failed to claim bonus",
  }).then((res) => {
    track("bonus_claim");
    return res;
  });
};

export const cancelGrant = async (grantId: string): Promise<GrantSummary> => {
  return apiRequest({
    method: "POST",
    url: API_ENDPOINTS.PROMOTION.CANCEL(grantId),
    errorMessage: "Failed to cancel bonus",
  });
};

export const redeemPromoCode = async (code: string): Promise<PromoCodeResult> => {
  return apiRequest({
    method: "POST",
    url: API_ENDPOINTS.PROMOTION.REDEEM_CODE,
    data: { code },
    errorMessage: "Failed to redeem the code",
  });
};

export const claimSkyBonus = async (): Promise<SkyClaim> => {
  return apiRequest<SkyClaim>({
    method: "POST",
    url: API_ENDPOINTS.PROMOTION.SKY_CLAIM,
    errorMessage: "Failed to claim bonus",
  }).then((res) => {
    track("bonus_claim");
    return res;
  });
};
